import { useState } from "react";
import { CheckCircle2, Plus, Printer, Trash2 } from "lucide-react";
import type { SimulatorAppSurfaceProps } from "../AppSurface.js";

const printerTiles = [
  { name: "Frontoffice MFP", driver: "Universal PCL", port: "WSD-01", status: "Ready", default: true },
  { name: "Network Printer", driver: "Generic PCL", port: "IP_192.168.1.80", status: "Ready", default: false },
  { name: "PDF Evidence Writer", driver: "FD PDF", port: "PORTPROMPT:", status: "Ready", default: false }
];

export function PrintersApp({ activeMachine, app, onRunCommand }: SimulatorAppSurfaceProps) {
  const [selectedPrinter, setSelectedPrinter] = useState("Frontoffice MFP");
  const [printerName, setPrinterName] = useState("IntakePrinter");
  const [driver, setDriver] = useState("Generic PCL");
  const [port, setPort] = useState("IP_192.168.1.80");

  const selectedTile = printerTiles.find((printer) => printer.name === selectedPrinter) ?? printerTiles[0];

  async function addPrinter() {
    await onRunCommand({
      appId: app.id,
      commandType: "PRINTER_ADD",
      capability: "local.printer.write",
      label: "Add printer",
      payload: {
        driver,
        machineId: activeMachine,
        port,
        printerName
      }
    });
    setSelectedPrinter(printerName);
  }

  async function setDefault(nextPrinterName = selectedPrinter) {
    await onRunCommand({
      appId: app.id,
      commandType: "PRINTER_SET_DEFAULT",
      capability: "local.printer.write",
      label: "Set default printer",
      payload: {
        machineId: activeMachine,
        printerName: nextPrinterName
      }
    });
    setSelectedPrinter(nextPrinterName);
  }

  async function removePrinter(nextPrinterName = selectedPrinter) {
    await onRunCommand({
      appId: app.id,
      commandType: "PRINTER_REMOVE",
      capability: "local.printer.write",
      label: "Remove printer",
      payload: {
        machineId: activeMachine,
        printerName: nextPrinterName
      }
    });
  }

  return (
    <div className="fd-sim-app fd-sim-printers">
      <header className="fd-printers-topbar">
        <div className="fd-printers-breadcrumb">
          <span>Control Panel</span>
          <span>/</span>
          <span>Hardware</span>
          <span>/</span>
          <strong>Printers</strong>
        </div>
        <button type="button" onClick={() => void addPrinter()}>
          <Plus size={16} />
          Add printer
        </button>
      </header>
      <main className="fd-printers-content">
        <section className="fd-printers-section">
          <div className="fd-printers-section-title">
            <Printer size={18} />
            <strong>Printers</strong>
            <span>{printerTiles.length}</span>
          </div>
          <div className="fd-printer-grid">
            {printerTiles.map((printer) => (
              <button
                className={`fd-printer-tile${selectedPrinter === printer.name ? " is-selected" : ""}`}
                key={printer.name}
                type="button"
                onClick={() => setSelectedPrinter(printer.name)}
                onDoubleClick={() => void setDefault(printer.name)}
              >
                <span className="fd-printer-icon">
                  <Printer size={34} />
                  {printer.default ? <CheckCircle2 size={17} /> : null}
                </span>
                <strong>{printer.name}</strong>
                <small>{printer.status}</small>
                <span>{printer.driver}</span>
                {printer.default ? <em>Default</em> : null}
              </button>
            ))}
          </div>
        </section>
        <section className="fd-mmc-detail-panel">
          <header>
            <div>
              <p>Selected printer</p>
              <h3>{selectedPrinter}</h3>
            </div>
            <button type="button" onClick={() => void setDefault()}>
              <CheckCircle2 size={16} />
              Set default
            </button>
          </header>
          <div className="fd-printer-properties">
            <div><span>Driver</span><strong>{selectedTile.driver}</strong></div>
            <div><span>Port</span><strong>{selectedTile.port}</strong></div>
            <div><span>Status</span><strong>{selectedTile.status}</strong></div>
          </div>
          <div className="fd-command-form">
            <label>
              Printer name
              <input value={printerName} onChange={(event) => setPrinterName(event.target.value)} />
            </label>
            <label>
              Driver
              <input value={driver} onChange={(event) => setDriver(event.target.value)} />
            </label>
            <label>
              Port
              <input value={port} onChange={(event) => setPort(event.target.value)} />
            </label>
            <button type="button" onClick={() => void addPrinter()}>Add</button>
            <button type="button" onClick={() => void removePrinter()}>
              <Trash2 size={15} />
              Remove
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
